import {useCallback, useContext, useMemo, useState} from 'react';
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import {ColumnDef} from '@tanstack/react-table';
import identity from 'lodash/identity';
import {Link} from 'react-router-dom';

import {WorkspaceContext} from 'src/context';
import {OverviewRelationship, PageDataType} from 'src/types';
import {ListingPage} from 'src/components/ListingPage';
import {getRelationshipOverviewReport} from 'src/api/reports';
import {Layout} from 'src/components/Layout';
import {DEPENDENCY_PATH} from 'src/routes';

import './styles.css';

export const OverviewRelationshipList = (props: {
  isOpen: boolean;
  onClose: () => void;
  fromId: string;
  toId: string;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [items, setItems] = useState<Array<OverviewRelationship>>([]);
  const [pageData, setPageData] = useState<PageDataType>();
  const [displayName, setDisplayName] = useState('Loading');

  const {currentWorkspace} = useContext(WorkspaceContext);

  const columns = useMemo<ColumnDef<any>[]>(
      () => [
        {
          header: 'From',
          id: 'fromName',
          accessorFn: identity,
          enableSorting: false,
          cell: ({getValue}) => {
            const {fromId, fromName} = getValue();
            return (
              <Link to={DEPENDENCY_PATH + '?entityId=' + fromId}>{fromName}</Link>
            );
          },
        },
        {
          header: 'Relationship Type',
          accessorKey: 'relationshipType',
          enableSorting: false,
        },
        {
          header: 'To',
          id: 'toName',
          accessorFn: identity,
          enableSorting: false,
          cell: ({getValue}) => {
            const {toId, toName} = getValue();
            return (
              <Link to={DEPENDENCY_PATH + '?entityId=' + toId}>{toName}</Link>
            );
          },
        },
      ],
      [],
  );

  const fetchReport = useCallback(
      async (page = 0, pageSize = 10, sortBy = 'fromIdentity,asc') => {
        setIsLoading(true);
        const reportRes = await getRelationshipOverviewReport(
            page,
            pageSize,
            props.fromId,
            props.toId,
            currentWorkspace?.materializedViewDefinition.id,
        );
        setIsLoading(false);
        const {data, metaData} = reportRes;
        if (data) {
          setItems(data || []);
          setPageData(metaData ? metaData.pageData : undefined);
          setDisplayName(data[0].fromApplication.name + ' -> ' + data[0].toApplication.name);
        }
      },
      [],
  );

  return (
    <Modal
      isOpen={props.isOpen}
      fade={false}
      fullscreen
    >
      <ModalHeader toggle={props.onClose}>{displayName}</ModalHeader>
      <ModalBody>
        <Layout loading={isLoading}>
          <div style={{overflowX: 'scroll'}}>
            <ListingPage
              columns={columns}
              data={items}
              pageData={pageData}
              fetchData={fetchReport}
            />
          </div>
        </Layout>
      </ModalBody>
    </Modal>
  );
};
